import { buildAutoPipeRoutingIterations } from "@multiconsult-group/multiplacer-core";
import type { IAppThunk } from "../../../app/store";
import { setSelectedRoomsInRedux } from "../../designSlice";
import { clearUnsavedRoutingItems, upsertUnsavedRoutingItem } from "../unsavedRoutingSlice";
import { selectAutoPipeRoutingInput } from "./autoPipeRoutingSelectors";
import {
  clearAutoPipeRoutingRunStateInRedux,
  setAutoPipeRoutingConnectionPointInRedux,
  setAutoPipeRoutingModalOpenInRedux,
  setAutoPipeRoutingRunStateInRedux,
  setSelectedAutoPipeRoutingSystemTypeInRedux,
} from "../../pipeRoutingSlice";
import { autoPipeRoutingMockState } from "./autoPipeRouting.mock";

const AUTO_PIPE_ROUTING_ITERATION_DELAY_MS = 50;

export const thunkAutoPipeRouting = (): IAppThunk<Promise<void>> => {
  return async (dispatch, getState) => {
    const state = getState();
    const autoPipeRoutingInput = selectAutoPipeRoutingInput(state);
    const localId = `auto-pipe-routing:${new Date().toISOString()}`;

    dispatch(setAutoPipeRoutingModalOpenInRedux(false));
    dispatch(
      setAutoPipeRoutingRunStateInRedux({
        isPreparing: true,
        isRunning: false,
        currentIteration: 0,
        totalIterations: 0,
      })
    );

    await waitForPreparationUi();

    const routingGraphs = buildAutoPipeRoutingIterations(autoPipeRoutingInput);

    dispatch(
      setAutoPipeRoutingRunStateInRedux({
        isPreparing: false,
        isRunning: true,
        currentIteration: 0,
        totalIterations: routingGraphs.length,
      })
    );

    try {
      for (let index = 0; index < routingGraphs.length; index += 1) {
        const routingGraph = routingGraphs[index];

        dispatch(
          setAutoPipeRoutingRunStateInRedux({
            isPreparing: false,
            isRunning: true,
            currentIteration: index + 1,
            totalIterations: routingGraphs.length,
          })
        );

        dispatch(clearUnsavedRoutingItems());
        dispatch(
          upsertUnsavedRoutingItem({
            localId,
            routingGraph,
            createdAtIso: new Date().toISOString(),
          })
        );

        if (index < routingGraphs.length - 1) {
          await delay(AUTO_PIPE_ROUTING_ITERATION_DELAY_MS);
        }
      }
    } finally {
      dispatch(clearAutoPipeRoutingRunStateInRedux());
    }
  };
};

export const thunkAutoPipeRoutingWithMockData = (): IAppThunk<Promise<void>> => {
  return async (dispatch) => {
    dispatch(setSelectedRoomsInRedux(autoPipeRoutingMockState.selectedRooms));
    dispatch(setSelectedAutoPipeRoutingSystemTypeInRedux(autoPipeRoutingMockState.systemType));
    dispatch(setAutoPipeRoutingConnectionPointInRedux(autoPipeRoutingMockState.connectionPoints));

    await dispatch(thunkAutoPipeRouting());
  };
};

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

function waitForPreparationUi(): Promise<void> {
  return new Promise((resolve) => {
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        window.setTimeout(resolve, 120);
      });
    });
  });
}
